import { Markets } from '~api';
import { type BucketRange, inBuckets } from '~portal/lib/buckets';
import {
  applyFilters,
  type ListingOptions,
  type ListingSearch,
  matchesQuery,
  parseListingSearch,
  type ResolvedListing,
  resolveListingSearch,
  sortBy,
  stringsParam,
} from '~portal/lib/listing';
import { type DirectoryEntry, joinList } from './-markets';

/**
 * Pure state and filtering for the regions listing (`/regions`). The shared listing fields (view,
 * sort, items per page, page, search) come from `~portal/lib/listing`; this module adds the
 * regions-only ones — the tab, the chosen continent, the market types, "Contains operators" and
 * the "Total market GGR" buckets — with the
 * same rules: every default is omitted from the URL, anything invalid is dropped rather than
 * thrown, and any change but a page change returns the reader to page 1.
 *
 * The rows are the warehouse's markets as directory entries (`-markets.ts`). The players and
 * player-value buckets have no source (workbook D12), so they are not URL state at all.
 */

export const REGION_SORTS = ['ggr', 'name', 'growth', 'updated'] as const;
export type RegionSort = (typeof REGION_SORTS)[number];

/** @desc Market GGR, highest first, is the default order (D-6); "Recently updated" is offered. */
export const REGION_LISTING: ListingOptions<RegionSort> = {
  sorts: REGION_SORTS,
  defaultSort: 'ggr',
};

export const REGION_SORT_OPTIONS: ReadonlyArray<{ value: RegionSort; label: string }> = [
  { value: 'ggr', label: 'Market GGR' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'name', label: 'Name' },
  { value: 'growth', label: 'Growth' },
];

export const REGION_TABS = ['market', 'continent', 'within'] as const;
export type RegionTab = (typeof REGION_TABS)[number];

/** @desc Figma's labels (R2). */
export const REGION_TAB_LABELS: Record<RegionTab, string> = {
  market: 'By market',
  continent: 'By continent',
  within: 'Within continent',
};

export const DEFAULT_TAB: RegionTab = 'market';

/** @desc The "Market type" chips: every kind on by default, so "By market" lists all 152 rows. */
export const MARKET_KINDS: readonly Markets.Kind[] = Markets.ALL_KINDS;

export const MARKET_KIND_LABELS: Record<Markets.Kind, string> = {
  country: 'Countries',
  state: 'States',
  'roll-up': 'Roll-ups',
};

/** @desc The regions-only URL fields, sparse like the shared ones. */
export type RegionFilters = {
  tab?: Exclude<RegionTab, 'market'>;
  continent?: string;
  ggrBucket?: string[];
  /** @desc "Contains operators": portal group slugs; a market matches any of them. */
  operator?: string[];
  /**
   * @desc Market types switched off. Stored as the exclusions so the default (every type on) is
   * the empty, omitted value, and switching every type off is still expressible.
   */
  hideKinds?: Markets.Kind[];
};

export type RegionsSearch = ListingSearch<RegionSort> & RegionFilters;

export type ResolvedRegionsSearch = ResolvedListing<RegionSort> & {
  tab: RegionTab;
  continent?: string;
  ggrBucket: string[];
  operator: string[];
  /** @desc The market types shown — the chips' value. */
  kinds: Markets.Kind[];
};

/**
 * @desc A bucket key as `bucketsFromEdges` spells it (`lt100m`, `100m-250m`, `gte5b`). The edges
 * are live, so the URL is checked for shape only; a key the current edges do not produce is
 * ignored when filtering and counting.
 */
const BUCKET_KEY = /^(lt|gte)?\d+(\.\d+)?[kmb]?(-\d+(\.\d+)?[kmb]?)?$/;

/** @desc The route's `validateSearch`. Never throws; defaults (and empty filters) are omitted. */
export const parseRegionsSearch = (raw: Record<string, unknown>): RegionsSearch => {
  const search: RegionsSearch = parseListingSearch(raw, REGION_LISTING);
  const tab = REGION_TABS.find((t) => t === raw.tab);
  if (tab && tab !== DEFAULT_TAB) search.tab = tab;
  // The continent only means something on the "Within continent" tab.
  if (tab === 'within' && typeof raw.continent === 'string' && raw.continent.trim()) {
    search.continent = raw.continent.trim();
  }
  const ggr = stringsParam(raw.ggrBucket).filter((key) => BUCKET_KEY.test(key));
  if (ggr.length) search.ggrBucket = ggr;
  const operator = stringsParam(raw.operator);
  if (operator.length) search.operator = operator;
  // A patch names the kinds shown (`kinds`); the URL keeps the kinds hidden (`hideKinds`).
  const shown = raw.kinds === undefined ? null : stringsParam(raw.kinds);
  const hidden = shown
    ? MARKET_KINDS.filter((kind) => !shown.includes(kind))
    : MARKET_KINDS.filter((kind) => stringsParam(raw.hideKinds).includes(kind));
  if (hidden.length) search.hideKinds = hidden;
  return search;
};

/** @desc Every default filled in, for rendering. */
export const resolveRegionsSearch = (search: RegionsSearch): ResolvedRegionsSearch => ({
  ...resolveListingSearch(search, REGION_LISTING),
  tab: search.tab ?? DEFAULT_TAB,
  ...(search.continent ? { continent: search.continent } : {}),
  ggrBucket: search.ggrBucket ?? [],
  operator: search.operator ?? [],
  kinds: MARKET_KINDS.filter((kind) => !(search.hideKinds ?? []).includes(kind)),
});

export type RegionsSearchPatch = Partial<ResolvedRegionsSearch> & { continent?: string };

/**
 * @desc Applies a change to the URL state. Anything but a page change sends the reader back to
 * page 1 — a page number means nothing once the set or its order has changed.
 */
export const withRegionsSearch = (
  current: RegionsSearch,
  patch: RegionsSearchPatch,
): RegionsSearch => {
  const resetsPage = Object.keys(patch).some((key) => key !== 'page');
  return parseRegionsSearch({ ...current, ...patch, ...(resetsPage ? { page: 1 } : {}) });
};

/** @desc The patch that clears the filter sheet, keeping tab, view, sort and items per page. */
export const CLEARED_FILTERS: RegionsSearchPatch = {
  ggrBucket: [],
  operator: [],
  kinds: [...MARKET_KINDS],
};

/** @desc The live bucket response as the five inner edges `bucketsFromEdges` takes. */
export const ggrEdges = (buckets: Markets.FilterBuckets | undefined): number[] =>
  (buckets?.buckets ?? [])
    .slice(1)
    .map((bucket) => bucket.minUsd)
    .filter((edge): edge is number => edge !== null);

/** @desc The chosen bucket keys the current edges produce. */
const knownBuckets = (keys: readonly string[], buckets: readonly BucketRange[]) =>
  keys.filter((key) => buckets.some((bucket) => bucket.key === key));

/** @desc Selected filter values — the n in "Filters applied (n)". Search is counted apart. */
export const activeFilterCount = (
  search: Pick<ResolvedRegionsSearch, 'ggrBucket' | 'kinds' | 'operator'>,
  ggrBuckets: readonly BucketRange[],
): number =>
  knownBuckets(search.ggrBucket, ggrBuckets).length +
  (MARKET_KINDS.length - search.kinds.length) +
  search.operator.length;

/** @desc The continents, biggest first — the continent tab's order and the picker's options. */
export const sortedContinents = (continents: readonly DirectoryEntry[]): DirectoryEntry[] =>
  sortBy(continents, (c) => c.onlineGgrUsd, 'desc');

/** @desc The chosen continent on the "Within continent" tab: the URL's, or the biggest. */
export const selectedContinent = (
  continents: readonly DirectoryEntry[],
  slug: string | undefined,
): DirectoryEntry | undefined => {
  const sorted = sortedContinents(continents);
  return sorted.find((c) => c.slug === slug) ?? sorted[0];
};

/**
 * @desc The rows a tab lists, before filters: every market (countries, states and roll-ups) on
 * "By market", the continents on "By continent", the chosen continent's markets on "Within
 * continent".
 */
export const entriesForTab = (
  markets: readonly DirectoryEntry[],
  continents: readonly DirectoryEntry[],
  tab: RegionTab,
  continentSlug?: string,
): DirectoryEntry[] => {
  if (tab === 'continent') return [...continents];
  if (tab === 'within') {
    const chosen = selectedContinent(continents, continentSlug);
    return markets.filter((market) => market.continentSlug === chosen?.slug);
  }
  return [...markets];
};

/**
 * @desc Filters, searches and sorts a tab's rows. GGR bucket choices are alternatives (OR), as are
 * market types and operators; the three intersect. A row with no GGR figure matches no bucket; a
 * market with no known presence matches no operator. Like the market types, operators narrow
 * markets only, never continents. Missing sort figures sort last in either direction.
 */
export const filterAndSortEntries = (
  rows: readonly DirectoryEntry[],
  search: Pick<ResolvedRegionsSearch, 'q' | 'ggrBucket' | 'kinds' | 'sort'> &
    Partial<Pick<ResolvedRegionsSearch, 'operator'>>,
  ggrBuckets: readonly BucketRange[],
): DirectoryEntry[] => {
  const keys = knownBuckets(search.ggrBucket, ggrBuckets);
  const operators = search.operator ?? [];
  const filtered = applyFilters(rows, [
    search.q !== '' && ((r) => matchesQuery(search.q, r.name, r.eyebrow, r.code)),
    // Continents are not a market type; the chips only narrow markets.
    search.kinds.length < MARKET_KINDS.length &&
      ((r) => r.kind === 'continent' || search.kinds.includes(r.kind)),
    keys.length > 0 &&
      ((r) => r.onlineGgrUsd !== null && inBuckets(r.onlineGgrUsd, keys, ggrBuckets)),
    operators.length > 0 &&
      ((r) =>
        r.kind === 'continent' ||
        (r.operatorsPresent ?? []).some((slug) => operators.includes(slug))),
  ]);
  if (search.sort === 'name') return sortBy(filtered, (r) => r.name, 'asc');
  if (search.sort === 'growth') return sortBy(filtered, (r) => r.yoyGrowth, 'desc');
  if (search.sort === 'updated') return sortBy(filtered, (r) => r.updatedAt?.getTime(), 'desc');
  return sortBy(filtered, (r) => r.onlineGgrUsd, 'desc');
};

/**
 * @desc What "Contains operators" filters on: live licensed-entity presence (Lane M) for the markets
 * with rows, the fixture's operators present elsewhere — so the section keeps its badge.
 */
export const operatorsNote = (liveMarkets: readonly string[]): string =>
  liveMarkets.length > 0
    ? `Live licensed-entity data for ${joinList(liveMarkets)}; illustrative elsewhere.`
    : 'From illustrative data until licensed-entity rows cover a market.';
